/**
 * Onboarding Wizard - Main container orchestrating the e-learning flow
 *
 * Manages navigation between modules, save/exit functionality,
 * and coordinates with the store for progress persistence.
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { X, ChevronLeft, ChevronRight, Save } from 'lucide-react';
import { useOnboardingStore } from '../../stores/onboardingStore';
import { useOnboardingTelemetry } from '../../hooks/useOnboardingTelemetry';
import { getStepsForRole, areHardGateStepsComplete } from '../../lib/onboarding/steps';
import OnboardingProgress from './OnboardingProgress';
import OnboardingStep from './OnboardingStep';
import IntroAnimation from './IntroAnimation';
import WelcomeLanding from './WelcomeLanding';
import LegalModule from './LegalModule';
import SecurityModule from './SecurityModule';
import SystemEducationModule from './SystemEducationModule';
import DashboardTourModule from './DashboardTourModule';
import CertificationModule from './CertificationModule';
import { cn } from '../../lib/utils';

// Storage key to track if intro has been seen this session
const INTRO_SEEN_KEY = 'lukhas-intro-seen';

interface OnboardingWizardProps {
  isDelta?: boolean;
  previousVersion?: string;
}

export default function OnboardingWizard({
  isDelta = false,
  previousVersion,
}: OnboardingWizardProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [showIntro, setShowIntro] = useState(false);

  const {
    role,
    currentModule,
    completedModules,
    isComplete,
    nextModule,
    prevModule,
    goToModule,
    completeModule,
  } = useOnboardingStore();

  // Check if we should show the intro animation
  useEffect(() => {
    const skipIntro = searchParams.get('skipIntro') === 'true';
    const forceIntro = searchParams.get('intro') === 'true';
    const introSeen = sessionStorage.getItem(INTRO_SEEN_KEY);

    // Force intro replay if ?intro=true is set
    if (forceIntro) {
      sessionStorage.removeItem(INTRO_SEEN_KEY);
      setShowIntro(true);
      return;
    }

    // Show intro only if:
    // - Not skipped via URL param
    // - Not already seen this session
    // - No existing role (fresh start)
    // - Not in delta mode
    if (!skipIntro && !introSeen && !role && !isDelta) {
      setShowIntro(true);
    }
  }, [searchParams, role, isDelta]);

  // Handle intro completion
  const handleIntroComplete = useCallback(() => {
    sessionStorage.setItem(INTRO_SEEN_KEY, 'true');
    setShowIntro(false);
  }, []);

  const { trackDropOff, flushEvents } = useOnboardingTelemetry();

  // Get steps for current role
  const steps = useMemo(() => {
    if (!role) return [];
    return getStepsForRole(role);
  }, [role]);

  // Current step
  const currentStep = steps[currentModule];

  // Check if hard gate is complete (can access dashboard)
  const hardGateComplete = useMemo(() => {
    if (!role) return false;
    return areHardGateStepsComplete(role, completedModules);
  }, [completedModules, role]);

  // Handle module completion
  const handleModuleComplete = useCallback(() => {
    if (!currentStep) return;

    completeModule(currentStep.id);

    // Check if this was the last step
    if (currentModule < steps.length - 1) {
      nextModule();
    } else {
      // All done - will show certification
    }
  }, [currentStep, currentModule, steps.length, completeModule, nextModule]);

  // Handle navigation to specific module
  const handleModuleClick = useCallback(
    (index: number) => {
      goToModule(index);
    },
    [goToModule]
  );

  // Handle save & exit
  const handleSaveAndExit = useCallback(async () => {
    if (currentStep) {
      trackDropOff(currentStep.id, 'user_exit');
    }
    await flushEvents();
    navigate('/');
  }, [currentStep, trackDropOff, flushEvents, navigate]);

  // Handle exit confirmation
  const handleExitClick = () => {
    if (hardGateComplete) {
      // Can exit without confirmation after hard gate
      navigate('/');
    } else {
      setShowExitConfirm(true);
    }
  };

  // Show intro animation first (for fresh visits)
  if (showIntro) {
    return (
      <div className="onboarding-theme">
        <IntroAnimation onComplete={handleIntroComplete} duration={12000} />
      </div>
    );
  }

  // If no role selected yet, show welcome
  if (!role) {
    return (
      <WelcomeLanding
        onComplete={() => {
          // Role is set in the component, just need to trigger re-render
        }}
      />
    );
  }

  // Render current module content
  const renderModuleContent = () => {
    if (!currentStep) return null;

    const commonProps = {
      onComplete: handleModuleComplete,
      role,
    };

    switch (currentStep.id) {
      case 'welcome':
        return <WelcomeLanding {...commonProps} />;
      case 'legal':
        return <LegalModule {...commonProps} />;
      case 'security':
        return <SecurityModule {...commonProps} />;
      case 'system-education':
        return <SystemEducationModule {...commonProps} />;
      case 'dashboard-tour':
        return <DashboardTourModule {...commonProps} />;
      case 'certification':
        return <CertificationModule {...commonProps} />;
      default:
        return null;
    }
  };

  return (
    <div className="onboarding-theme min-h-screen flex flex-col">
      {/* Header */}
      <header
        className="sticky top-0 z-50 px-6 py-4 border-b backdrop-blur-lg"
        style={{
          backgroundColor: 'rgba(var(--color-bg-rgb), 0.9)',
          borderColor: 'var(--color-border)',
        }}
      >
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          {/* Logo & Progress */}
          <div className="flex items-center gap-4">
            <span className="lambda-symbol text-2xl">
              Λ
            </span>
            <OnboardingProgress
              role={role}
              currentModule={currentModule}
              completedModules={completedModules}
              onModuleClick={handleModuleClick}
              compact
            />
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {/* Save Progress Note */}
            <span
              className="text-xs flex items-center gap-1 mr-2"
              style={{ color: 'var(--color-text-muted)' }}
            >
              <Save className="w-3 h-3" />
              Auto-saved
            </span>

            {/* Exit Button */}
            <button
              onClick={handleExitClick}
              className="p-2 rounded-lg transition-colors hover:bg-opacity-10"
              style={{ color: 'var(--color-text-muted)' }}
              aria-label="Exit onboarding"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {/* Delta Mode Banner */}
        {isDelta && previousVersion && (
          <div
            className="px-6 py-3 text-center text-sm"
            style={{
              backgroundColor: 'var(--color-accent-muted)',
              color: 'var(--color-accent)',
            }}
          >
            Some policies have been updated since your last visit. Please review the changes.
          </div>
        )}

        {/* Progress Stepper (Full) */}
        <div className="px-6 py-8 border-b" style={{ borderColor: 'var(--color-border)' }}>
          <OnboardingProgress
            role={role}
            currentModule={currentModule}
            completedModules={completedModules}
            onModuleClick={handleModuleClick}
          />
        </div>

        {/* Module Content */}
        <div className="flex-1">
          <OnboardingStep
            step={currentStep}
            isFirst={currentModule === 0}
            isLast={currentModule === steps.length - 1}
            onPrev={() => prevModule()}
            onNext={handleModuleComplete}
          >
            {renderModuleContent()}
          </OnboardingStep>
        </div>
      </main>

      {/* Footer Navigation */}
      <footer
        className="sticky bottom-0 px-6 py-4 border-t backdrop-blur-lg"
        style={{
          backgroundColor: 'rgba(var(--color-bg-rgb), 0.9)',
          borderColor: 'var(--color-border)',
        }}
      >
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          {/* Back Button */}
          <button
            onClick={() => prevModule()}
            disabled={currentModule === 0}
            className={cn(
              'flex items-center gap-2 px-4 py-2 rounded-lg transition-all',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
            style={{
              backgroundColor: 'var(--color-bg-secondary)',
              color: 'var(--color-text-secondary)',
            }}
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </button>

          {/* Step Indicator */}
          <span
            className="text-sm"
            style={{ color: 'var(--color-text-muted)' }}
          >
            {currentModule + 1} of {steps.length}
          </span>

          {/* Next/Complete Button */}
          <button
            onClick={handleModuleComplete}
            className={cn(
              'flex items-center gap-2 px-6 py-2 rounded-lg transition-all',
              'hover:scale-105 active:scale-100'
            )}
            style={{
              backgroundColor: 'var(--color-accent)',
              color: 'var(--color-bg)',
            }}
          >
            {currentModule === steps.length - 1 ? 'Complete' : 'Continue'}
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </footer>

      {/* Exit Confirmation Modal */}
      {showExitConfirm && (
        <ExitConfirmModal
          onConfirm={handleSaveAndExit}
          onCancel={() => setShowExitConfirm(false)}
          hardGateComplete={hardGateComplete}
        />
      )}
    </div>
  );
}

// ============================================================================
// Exit Confirmation Modal
// ============================================================================

interface ExitConfirmModalProps {
  onConfirm: () => void;
  onCancel: () => void;
  hardGateComplete: boolean;
}

function ExitConfirmModal({
  onConfirm,
  onCancel,
  hardGateComplete,
}: ExitConfirmModalProps) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
    >
      <div
        className="max-w-md w-full p-6 rounded-2xl"
        style={{ backgroundColor: 'var(--color-bg-secondary)' }}
      >
        <h3
          className="text-lg font-semibold mb-2"
          style={{ color: 'var(--color-text-primary)' }}
        >
          Save & Exit?
        </h3>
        <p
          className="text-sm mb-6"
          style={{ color: 'var(--color-text-secondary)' }}
        >
          Your progress is saved automatically. You can resume anytime from where you left off.
          {!hardGateComplete && (
            <span
              className="block mt-2 font-medium"
              style={{ color: 'var(--color-warning)' }}
            >
              Note: You'll need to complete modules 1-3 to access the dashboard.
            </span>
          )}
        </p>
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 rounded-lg transition-colors"
            style={{
              backgroundColor: 'var(--color-bg-tertiary)',
              color: 'var(--color-text-secondary)',
            }}
          >
            Continue Training
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 px-4 py-2 rounded-lg transition-colors"
            style={{
              backgroundColor: 'var(--color-accent)',
              color: 'var(--color-bg)',
            }}
          >
            Save & Exit
          </button>
        </div>
      </div>
    </div>
  );
}
